import { createBox, createText, createTheme } from '@shopify/restyle';

import { lightPalette, darkPalette } from './colors';
import { COLOR_THEME, MAIN_THEME } from './types';
import { fontSize, lineHeight, scale } from './primitives';
import { generateColorPalette } from './helpers';
import { Alert, GestureResponderEvent, Platform, Pressable, TouchableOpacity } from 'react-native';
import { useCallback } from 'react';
import { trackEvent } from '../utils/amplitude';
import { useAppSelector } from '@app/store/hooks';
import { isFromTDLPlatform } from '@app/utils';
import { getThemeColorSetBasedOnPlatformCode } from './dezerv';

const baseTheme = {
  // margin [m], marginTop [mt], marginRight [mr], marginBottom [mb]
  // marginLeft [ml], marginStart [ms], marginEnd[me], marginHorizontal [mx]
  // marginVertical [my], padding [p], paddingTop [pt], paddingRight [pr], paddingBottom [pb]
  // paddingLeft [pl], paddingStart [ps], paddingEnd [pe], paddingHorizontal [px], paddingVertical [py], gap [g], rowGap [rG], columnGap [cG]
  spacing: {
    '2xs': scale['scale-2'],
    xs: scale['scale-4'],
    sm: scale['scale-8'],
    md: scale['scale-12'],
    lg: scale['scale-16'],
    xl: scale['scale-20'],
    '2xl': scale['scale-24'],
    '3xl': scale['scale-28'],
    '4xl': scale['scale-32'],
    '5xl': scale['scale-40'],
    '6xl': scale['scale-44'],
    '7xl': scale['scale-48'],
    '8xl': scale['scale-52'],
  },

  //borderRadius, borderBottomLeftRadius, borderBottomRightRadius,
  //borderBottomStartRadius, borderBottomEndRadius, borderTopLeftRadius
  //borderTopRightRadius, borderTopStartRadius, borderTopEndRadius
  borderRadii: {
    '2xs': scale['scale-2'],
    xs: scale['scale-4'],
    sm: scale['scale-8'],
    md: scale['scale-12'],
    lg: scale['scale-16'],
    xl: scale['scale-20'],
    '2xl': scale['scale-24'],
    '3xl': scale['scale-28'],
    full: scale['scale-999'],
  },
};

// Now, define the type for the colorsForSuperset object
type ColorsForSuperset = {
  [key in
  | 'white'
  | 'black'
  | 'text-primary'
  | 'text-secondary'
  | 'text-placeholder'
  | 'text-disabled'
  | 'text-emphasis-secondary'
  | 'text-action-primary'
  | 'text-action-primary-hover'
  | 'text-on-action-primary'
  | 'text-success'
  | 'text-warning'
  | 'text-info'
  | 'text-error'
  | 'bg-surface-page'
  | 'bg-surface-primary'
  | 'bg-surface-secondary'
  | 'bg-surface-emphasis-primary'
  | 'bg-surface-emphasis-secondary'
  | 'bg-fill-primary'
  | 'bg-fill-secondary'
  | 'bg-fill-emphasis-primary-1'
  | 'bg-fill-emphasis-primary-2'
  | 'bg-fill-emphasis-secondary'
  | 'bg-fill-success'
  | 'bg-fill-warning'
  | 'bg-fill-error'
  | 'bg-fill-info'
  | 'bg-fill-blue-light'
  | 'bg-action-primary'
  | 'bg-action-primary-hover'
  | 'bg-action-disabled'
  | 'border-primary'
  | 'border-secondary'
  | 'border-form'
  | 'border-success'
  | 'border-warning'
  | 'border-info'
  | 'border-error'
  | 'border-action'
  | 'border-action-hover'
  | 'border-focus'
  | 'border-disabled'
  | 'icon-primary'
  | 'icon-secondary'
  | 'icon-action-primary'
  | 'icon-on-action-primary'
  | 'icon-emphasis-secondary'
  | 'icon-success'
  | 'icon-warning'
  | 'icon-info'
  | 'icon-error'
  | 'icon-emphasis-primary'
  | 'icon-disabled'
  | 'icon-primary-hover'
  ]: string;
};


let themeColorSet: ColorsForSuperset
const isFromTDL = isFromTDLPlatform();
export const themeTextVariants = {
  // heading variants
  h1b: {
    fontSize: fontSize['font-size-4xl'],
    lineHeight: lineHeight['font-line-height-5xl'],
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-Bold',
  },
  h1s: {
    fontSize: fontSize['font-size-3xl'],
    lineHeight: lineHeight['font-line-height-4xl'],
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-SemiBold',
  },
  h2b: {
    fontSize: fontSize['font-size-2xl'],
    lineHeight: lineHeight['font-line-height-3xl'],
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-Bold',
  },
  h2s: {
    fontSize: fontSize['font-size-2xl'],
    lineHeight: lineHeight['font-line-height-3xl'],
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-SemiBold',
  },
  h3b: {
    fontSize: fontSize['font-size-xl'],
    lineHeight: lineHeight['font-line-height-2xl'],
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-Bold',
  },
  h3s: {
    fontSize: fontSize['font-size-xl'],
    lineHeight: lineHeight['font-line-height-2xl'],
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-SemiBold',
  },
  h4b: {
    fontSize: fontSize['font-size-lg'],
    lineHeight: lineHeight['font-line-height-xl'],
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-Bold',
  },
  h4s: {
    fontSize: fontSize['font-size-lg'],
    lineHeight: lineHeight['font-line-height-xl'],
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-SemiBold',
  },
  h5b: {
    fontSize: fontSize['font-size-md'],
    lineHeight: lineHeight['font-line-height-lg'],
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-Bold',
  },
  h5s: {
    fontSize: fontSize['font-size-md'],
    lineHeight: lineHeight['font-line-height-lg'],
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-SemiBold',
  },
  h6b: {
    fontSize: fontSize['font-size-sm'],
    lineHeight: lineHeight['font-line-height-md'],
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-Bold',
  },
  h6s: {
    fontSize: fontSize['font-size-sm'],
    lineHeight: lineHeight['font-line-height-md'],
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Inter' : 'Inter-SemiBold',
  },

  // body variants

  b1b: {
    fontSize: fontSize['font-size-lg'],
    lineHeight: lineHeight['font-line-height-2xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-ExtraBold',
    fontWeight: '700',
  },
  b1s: {
    fontSize: fontSize['font-size-lg'],
    lineHeight: lineHeight['font-line-height-2xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Bold',
    fontWeight: '600',
  },
  b1m: {
    fontSize: fontSize['font-size-lg'],
    lineHeight: lineHeight['font-line-height-2xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-SemiBold',
    fontWeight: '500',
  },
  b1r: {
    fontSize: fontSize['font-size-lg'],
    lineHeight: lineHeight['font-line-height-2xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Regular',
    fontWeight: '400',
  },
  b2b: {
    fontSize: fontSize['font-size-md'],
    lineHeight: lineHeight['font-line-height-xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-ExtraBold',
    fontWeight: '700',
  },
  b2s: {
    fontSize: fontSize['font-size-md'],
    lineHeight: lineHeight['font-line-height-xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Bold',
    fontWeight: '600',
  },
  b2m: {
    fontSize: fontSize['font-size-md'],
    lineHeight: lineHeight['font-line-height-xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-SemiBold',
    fontWeight: '500',
  },
  b2r: {
    fontSize: fontSize['font-size-md'],
    lineHeight: lineHeight['font-line-height-xl'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Regular',
    fontWeight: '400',
  },
  b3b: {
    fontSize: fontSize['font-size-sm'],
    lineHeight: lineHeight['font-line-height-lg'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-ExtraBold',
    fontWeight: '700',
  },
  b3s: {
    fontSize: fontSize['font-size-sm'],
    lineHeight: lineHeight['font-line-height-lg'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Bold',
    fontWeight: '600',
  },
  b3m: {
    fontSize: fontSize['font-size-sm'],
    lineHeight: lineHeight['font-line-height-lg'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-SemiBold',
    fontWeight: '500',
  },
  b3r: {
    fontSize: fontSize['font-size-sm'],
    lineHeight: lineHeight['font-line-height-lg'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Regular',
    fontWeight: '400',
  },
  b4b: {
    fontSize: fontSize['font-size-xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-ExtraBold',
    fontWeight: '700',
  },
  b4s: {
    fontSize: fontSize['font-size-xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Bold',
    fontWeight: '600',
  },
  b4m: {
    fontSize: fontSize['font-size-xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-SemiBold',
    fontWeight: '500',
  },
  b4r: {
    fontSize: fontSize['font-size-xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Regular',
    fontWeight: '400',
  },
  b5b: {
    fontSize: fontSize['font-size-2xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-ExtraBold',
    fontWeight: '700',
  },
  b5s: {
    fontSize: fontSize['font-size-2xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Bold',
    fontWeight: '600',
  },
  b5m: {
    fontSize: fontSize['font-size-2xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-SemiBold',
    fontWeight: '500',
  },
  b5r: {
    fontSize: fontSize['font-size-2xs'],
    lineHeight: lineHeight['font-line-height-sm'],
    fontFamily: Platform.OS === 'web' ? isFromTDL ? 'InterSimonson' : 'Inter' : 'Inter-Regular',
    fontWeight: '400',
  },

  // label variants
  l1: {
    fontSize: fontSize['font-size-3xl'],
    lineHeight: lineHeight['font-line-height-5xl'],
    fontFamily: isFromTDL ? 'InterSimonson' : 'Inter',
    fontWeight: '700',
  },
  l2: {
    fontSize: fontSize['font-size-2xl'],
    lineHeight: lineHeight['font-line-height-3xl'],
    fontFamily: isFromTDL ? 'InterSimonson' : 'Inter',
    fontWeight: '600',
  },
  l5: {
    fontFamily: isFromTDL ? 'InterSimonson' : 'Inter',
    fontSize: fontSize['font-size-md'],
    lineHeight: lineHeight['font-line-height-lg'],
    fontWeight: '700',
  },

  // default style fallback when nothing is provided as props explicitly
  defaults: {
    color: 'text-primary',
    fontFamily: isFromTDL ? 'MyCustomFont' : 'Poppins',
    fontSize: fontSize['font-size-lg'],
    lineHeight: lineHeight['font-line-height-2xl'],
  },
}

// Generates final color object.
// if Core & DSP Provided it picks respective color from palette.
// Supports passing a primary and secondary color and build primary and secondary palette based of that
const buildColorObj = (superset: MAIN_THEME, mode: COLOR_THEME, primaryColor?: string | undefined, secondaryColor?: string | undefined, platform?: string) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;


  //Requested primary palette
  const { light: plight, dark: pdark } = primaryColor ? generateColorPalette('#' + primaryColor) : { light: undefined, dark: undefined };
  const customPrimaryPalette = mode === 'dark' ? pdark : plight


  //Requested secondary palette
  const { light: slight, dark: sdark } = secondaryColor ? generateColorPalette('#' + secondaryColor) : { light: undefined, dark: undefined };
  const customSecondaryPalette = mode === 'dark' ? sdark : slight



  const defaultPrimaryPalette = customPrimaryPalette ?? (superset === 'core' ? palette['blue'] : palette['turquoise']);
  const defaultSecondaryPalette = customSecondaryPalette ?? (superset === 'core' ? palette['green'] : palette['grey']);



  const colorsForSuperset = {
    'white': palette.white,
    'black': palette.black,
    //text colors
    'milk': palette.milk,
    'chalk': palette.chalk,
    'text-primary': palette.grey[1200],
    'text-secondary': palette.grey[1100],
    'text-placeholder': palette.grey[900],
    'text-disabled': palette.grey[900],
    'text-emphasis-secondary': defaultSecondaryPalette[1100],
    'text-action-primary': defaultPrimaryPalette[1100],
    'text-action-primary-hover': defaultPrimaryPalette[900],
    'text-on-action-primary': palette.white,
    'text-success': palette.green[1100],
    'text-warning': palette.yellow[700],
    'text-info': palette.blue[1200],
    'text-error': palette.red[1100],

    //bg-colors
    'bg-surface-page': isFromTDLPlatform() ? '#EBF0FF' : defaultPrimaryPalette[200],
    'bg-surface-primary': palette.grey[0],
    'bg-surface-secondary': palette.grey[200],
    'bg-surface-emphasis-primary': defaultPrimaryPalette[400],
    'bg-surface-emphasis-secondary': defaultSecondaryPalette[300],
    'bg-fill-primary': palette.grey[0],
    'bg-fill-secondary': palette.grey[200],
    'bg-fill-emphasis-primary-1': defaultPrimaryPalette[300],
    'bg-fill-emphasis-primary-2': defaultPrimaryPalette[900],
    'bg-fill-emphasis-secondary': defaultSecondaryPalette[400],
    'bg-fill-success': palette.green[400],
    'bg-fill-warning': palette.yellow[200],
    'bg-fill-error': palette.red[300],
    'bg-fill-info': palette.grey[200],
    'bg-fill-blue-light': palette.blue[300],
    'bg-action-primary': defaultPrimaryPalette[900],
    'bg-action-primary-hover': defaultPrimaryPalette[1000],
    'bg-action-disabled': palette.grey[200],

    //border
    'border-primary': palette.grey[500],
    'border-secondary': palette.grey[300],
    'border-emphasis-primary': palette.blue[700],
    'border-emphasis-secondary': palette.green[700],
    'border-form': palette.grey[700],
    'border-success': palette.green[800],
    'border-warning': palette.yellow[700],
    'border-info': palette.blue[700],
    'border-error': palette.red[800],
    'border-action': defaultPrimaryPalette[900],
    'border-action-hover': defaultPrimaryPalette[1000],
    'border-focus': defaultPrimaryPalette[900],
    'border-disabled': palette.grey[500],

    //icon

    'icon-primary': palette.grey[1200],
    'icon-secondary': palette.grey[1000],
    'icon-action-primary': defaultPrimaryPalette[1100],
    'icon-on-action-primary': palette.grey[0],
    'icon-emphasis-secondary': defaultSecondaryPalette[1100],
    'icon-success': palette.green[1100],
    'icon-warning': palette.yellow[700],
    'icon-info': palette.blue[1100],
    'icon-error': palette.red[900],
    'icon-emphasis-primary': palette.blue[500],
    'icon-disabled': palette.grey[400],
    'icon-primary-hover': palette.blue[200]
  }
  if (platform && platform !== '') {
    themeColorSet = getThemeColorSetBasedOnPlatformCode(colorsForSuperset, platform);
  } else {
    themeColorSet = colorsForSuperset;
  }
  return themeColorSet;
};


export const createDynamicTheme = (superset: MAIN_THEME, mode: COLOR_THEME, primaryColor?: string | null, secondaryColor?: string | null, platform?: string | null) => {
  return createTheme({
    colors: buildColorObj(superset, mode, primaryColor ?? undefined, secondaryColor ?? undefined, platform ?? undefined),
    spacing: baseTheme.spacing,
    borderRadii: baseTheme.borderRadii,
    textVariants: themeTextVariants
  });
};

export type Theme = ReturnType<typeof createDynamicTheme>;

const ThemedText = createText<Theme>();
const ThemedView = createBox<Theme>();
const ExtendedPressable = createBox<Theme, React.ComponentProps<typeof Pressable>>(Pressable);
const ThemedTouchableOpacity = createBox<Theme, React.ComponentProps<typeof TouchableOpacity>>(TouchableOpacity);

interface ExtendedPressableProps extends React.ComponentProps<typeof ExtendedPressable> {
  eventName?: string;
  additionalProperties?: Record<string, any>
}

const ThemedPressable = ({ onPress, eventName, children, additionalProperties, ...rest }: ExtendedPressableProps) => {

  const eventProperties = { ...(additionalProperties && { ...additionalProperties }) };
  const { isfromTDL } = useAppSelector(state => state.platformParams);

  const clickEventName = eventName + '_CLICKED'

  const handlePress = useCallback((event: GestureResponderEvent) => {
    if (eventName) {
      trackEvent(clickEventName, eventProperties)
    }
    if (onPress) {
      onPress(event);
    }
  }, [onPress, eventName])

  return (
    <ExtendedPressable onPress={handlePress} {...rest} {...isfromTDL && { borderRadius: "sm" }}>
      {children}
    </ExtendedPressable>
  )
}



export { ThemedText, ThemedView, ThemedPressable, themeColorSet };
export default createDynamicTheme;